import type { RenderComponentProps } from "replo-runtime/shared/renderComponent";
import type { Component } from "schemas/component";
import type { SelectedSellingPlanIdOrOneTimePurchase } from "schemas/product";
import type { ShopifySellingPlan } from "schemas/runtimeProduct";

import * as React from "react";

import { mergeContext } from "../../../shared/utils/context";
import { withLiquidAlternate } from "../../../shared/utils/withLiquidAlternate";
import { ReploComponent } from "../ReploComponent";
import ReploLiquidChunk from "../ReploLiquid/ReploLiquidChunk";

const ONE_TIME_PURCHASE_ID = "replo-one-time-purchase";
const ONE_TIME_PURCHASE_PLAN = {
  id: ONE_TIME_PURCHASE_ID,
  name: "One-Time Purchase",
  description: "",
  options: [],
  priceAdjustments: [],
} satisfies ShopifySellingPlan;

type SellingPlanId = typeof ONE_TIME_PURCHASE_ID | number;

const SellingPlanSelect = ({
  component,
  context,
  componentAttributes: attributes,
  extraAttributes,
}: RenderComponentProps) => {
  const includeOneTimePurchase =
    component.props._includeOneTimePurchase ?? true;
  const sellingPlans: ShopifySellingPlan[] =
    context.attributes?._sellingPlans ?? [];
  const selectedSellingPlans = component.props._productSellingPlans;
  const filteredSellingPlans = sellingPlans.filter((sellingPlan) => {
    return (
      !selectedSellingPlans ||
      selectedSellingPlans.includes(Number(sellingPlan.id))
    );
  });
  const template =
    component.children?.length &&
    component.children[component.children.length - 1];
  if (!template) {
    return null;
  }

  if (filteredSellingPlans.length === 0) {
    return null;
  }

  let selectedPlanId: SellingPlanId | null =
    context.state?.product?.selectedSellingPlan?.id ?? null;

  // Note (Fran: 2023-03-10): If the user enabled includeOneTimePurchase we need
  // to add one more selling plan to allow make a purchase only one time.
  // In this case the id will be null to not conflict with other selling plans.
  const allSellingPlans = [...filteredSellingPlans];
  if (includeOneTimePurchase) {
    selectedPlanId ??= ONE_TIME_PURCHASE_ID;
    allSellingPlans.unshift(ONE_TIME_PURCHASE_PLAN);
  }

  return (
    <div {...attributes} role="listbox">
      {allSellingPlans.map((sellingPlan, index) => {
        return (
          <SellingPlanComponent
            key={sellingPlan.id}
            component={component}
            template={template}
            extraAttributes={extraAttributes}
            sellingPlan={sellingPlan}
            context={context}
            index={index}
            isSelected={
              sellingPlan.id === ONE_TIME_PURCHASE_ID
                ? selectedPlanId === ONE_TIME_PURCHASE_ID
                : Number(sellingPlan.id) === Number(selectedPlanId)
            }
          />
        );
      })}
    </div>
  );
};

function SellingPlanComponent({
  component,
  template,
  extraAttributes,
  sellingPlan,
  context,
  index,
  isSelected,
  onClickOverride,
}: Pick<RenderComponentProps, "component" | "extraAttributes" | "context"> & {
  template: Component;
  sellingPlan?: ShopifySellingPlan;
  index: number;
  isSelected?: boolean;
  onClickOverride?: boolean;
}) {
  const childComponent = component.children?.[index] ?? template;
  if (!childComponent) {
    return null;
  }
  // NOTE (Matt 2025-04-01): The defaultPrice of a selling plan is primarily used
  // for OneTimePurchase.
  const defaultPrice = context.attributes?._product.variant.price;
  const nextContext = mergeContext(context, {
    attributes: {
      _currentSellingPlan: sellingPlan && {
        ...sellingPlan,
        price: sellingPlan.price ?? defaultPrice,
      },
    },
    state: {
      sellingPlanSelect: {
        isSelected,
      },
    },
  });

  let sellingPlanId: SelectedSellingPlanIdOrOneTimePurchase | undefined;
  // NOTE (Matt 2024-05-28): The onClickOverride attribute is used by the liquid
  // version of this component to ensure that all associated html attributes
  // of the onClick action are applied to the pre-hydrated HTML to reduce
  // differences upon hydration.
  if (sellingPlan?.id === ONE_TIME_PURCHASE_ID || onClickOverride) {
    sellingPlanId = { __reploOneTimePurchase: true };
  } else if (sellingPlan?.id != null) {
    sellingPlanId = Number(sellingPlan.id);
  }

  return (
    <ReploComponent
      component={childComponent}
      extraAttributes={{
        ...extraAttributes,
        role: "option",
        "aria-selected": isSelected ?? false,
      }}
      context={nextContext}
      defaultActions={
        sellingPlanId
          ? {
              actions: {
                onClick: [
                  {
                    id: `alchemy:selectSellingPlan`,
                    type: "setActiveSellingPlan",
                    value: { sellingPlanId },
                  },
                ],
              },
            }
          : undefined
      }
    />
  );
}

const SellingPlanSelectLiquid: React.FC<RenderComponentProps> = ({
  component,
  extraAttributes,
  componentAttributes: attributes,
  context,
}) => {
  const includeOneTimePurchase =
    component.props._includeOneTimePurchase ?? true;
  const templateIndex = component.children?.length
    ? component.children.length - 1
    : 0;
  const template = component.children?.[templateIndex];
  if (!template) {
    return null;
  }

  const selectedSellingPlans = component.props._productSellingPlans;
  const selectedSellingPlansString = selectedSellingPlans?.join(",") ?? "";

  // NOTE (Chance 2024-05-24): The liquid conditionals are a bit hard to read
  // without extra indentation
  // prettier-ignore
  return (
    <ReploLiquidChunk>
      {`{% if reploSortedSellingPlans != blank %}`}
      {`{% capture selectedSellingPlanIds %}${selectedSellingPlansString}{% endcapture %}`}
        <div {...attributes} role="listbox">
          {includeOneTimePurchase && (
            <>
              {`{% capture reploRepeatedSellingPlanName %}${ONE_TIME_PURCHASE_PLAN.name}{% endcapture %}`}
              {`{% if reploSelectedSellingPlan == blank %}`}
                <SellingPlanComponent
                  component={component}
                  template={template}
                  sellingPlan={ONE_TIME_PURCHASE_PLAN}
                  extraAttributes={extraAttributes}
                  context={context}
                  index={0}
                  isSelected={true}
                />
              {`{% else %}`}
                <SellingPlanComponent
                  component={component}
                  template={template}
                  sellingPlan={ONE_TIME_PURCHASE_PLAN}
                  extraAttributes={extraAttributes}
                  context={context}
                  index={0}
                  isSelected={false}
                />
              {`{% endif %}`}
            </>
          )}
          {`{% for reploRepeatedSellingPlan in reploSortedSellingPlans %}`}
          {`{% if selectedSellingPlanIds == blank or selectedSellingPlanIds contains reploRepeatedSellingPlan.id %}`}
            {`{% capture reploRepeatedSellingPlanName %}{{ reploRepeatedSellingPlan.name }}{% endcapture %}`}
            {/* NOTE (Chance 2024-05-23): If we have one-time purchase the
                index is 1-based because that plan always renders first */}
            {`{% case forloop.${includeOneTimePurchase ? "index" : "index0"} %}`}
              {component.children?.map((_, childIndex) => {
                return (
                  <>
                    {`{% when ${childIndex} %}`}
                    {`{% if reploSelectedSellingPlan == reploRepeatedSellingPlan %}`}
                    <SellingPlanComponent
                      component={component}
                      template={template}
                      extraAttributes={extraAttributes}
                      context={context}
                      index={childIndex}
                      isSelected={true}
                      onClickOverride
                    />
                    {`{% else %}`}
                    <SellingPlanComponent
                      component={component}
                      template={template}
                      extraAttributes={extraAttributes}
                      context={context}
                      index={childIndex}
                      isSelected={false}
                      onClickOverride
                    />
                    {`{% endif %}`}
                  </>
                );
              })}
            {`{% else %}`}
            {`{% if reploSelectedSellingPlan == reploRepeatedSellingPlan %}`}
              <SellingPlanComponent
                component={component}
                template={template}
                extraAttributes={extraAttributes}
                context={context}
                index={templateIndex}
                isSelected={true}
                onClickOverride
              />
            {`{% else %}`}
              <SellingPlanComponent
                component={component}
                template={template}
                extraAttributes={extraAttributes}
                context={context}
                index={templateIndex}
                isSelected={false}
                onClickOverride
              />
            {`{% endif %}`}
            {`{% endcase %}`}
          {`{% endif %}`}
          {`{% endfor %}`}
        </div>
      {`{% endif %}`}
    </ReploLiquidChunk>
  );
};

export default withLiquidAlternate(SellingPlanSelect, SellingPlanSelectLiquid);
